import React, {createContext} from 'react'
import styled from 'styled-components'
import {themeGet, Box, BoxProps, sx, SxProp} from '../..'

import createSlots from '../../utils/create-slots'

export const {Slots, Slot} = createSlots(['Footer', 'RightPane', 'LeftPane', 'Header'])

export const RuleBuilderLayoutContext = createContext<{
  isSplitPane?: boolean
} | null>(null)

export type RuleBuilderLayoutContextType = {
  isSplitPane?: boolean
} & RuleBuilderLayoutProps

export type RuleBuilderLayoutProps = {
  /**
   * Children to render in the layout
   */
  children?: React.ReactNode
  /**
   * Whether the layout should render as a 50/50 split pane; default: 40/60
   */
  isSplitPane?: boolean
} & BoxProps
// --------------------------------------------------------
// Root
const RuleBuilderLayout = ({children, isSplitPane = false, ...props}: RuleBuilderLayoutProps) => {
  const leftPaneChild = React.Children.toArray(children).find(child =>
    React.isValidElement(child) && child.type === LeftPane ? child : null
  )
  const rightPaneChild = React.Children.toArray(children).find(child =>
    React.isValidElement(child) && child.type === RightPane ? child : null
  )
  const footerChild = React.Children.toArray(children).find(child =>
    React.isValidElement(child) && child.type === Footer ? child : null
  )
  const headerChild = React.Children.toArray(children).find(child =>
    React.isValidElement(child) && child.type === Footer ? child : null
  )
  return (
    <>
      <Slots
        context={{
          isSplitPane
        }}
      >
        {slots => {
          return (
            <RuleBuilderLayoutContext.Provider value={{isSplitPane}}>
              <Wrapper>
                {headerChild ? <Box sx={{display: 'flex', width: '100%'}}>{slots.Header}</Box> : null}
                <Box sx={{display: 'flex', flex: '1 1 100%', flexWrap: 'wrap', maxWidth: '100%'}}>
                  {leftPaneChild ? slots.LeftPane : null}
                  {rightPaneChild ? slots.RightPane : null}
                </Box>

                {footerChild ? <Box sx={{display: 'flex', width: '100%'}}>{slots.Footer}</Box> : null}
                {React.Children.toArray(children).filter(child => React.isValidElement(child))}
              </Wrapper>
            </RuleBuilderLayoutContext.Provider>
          )
        }}
      </Slots>
    </>
  )
}

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  padding: 0;
`

// --------------------------------------------------------
// LeftPane
export type LeftPaneProps = {
  /**
   * Children to render in the nav
   */
  children?: React.ReactNode
  /**
   * Whether the layout should render as a 50/50 split pane; default: 40/60
   */
  isSplitPane?: boolean
} & BoxProps
/**
 * Left pane for Rule builder layout. Must be rendered inside of a Rule Builder
 */
const LeftPane = ({children, isSplitPane, ...props}: LeftPaneProps) => {
  return (
    <Slot name="LeftPane">
      {({isSplitPane}: RuleBuilderLayoutContextType) => {
        return (
          <StyledLeftPane isSplitPane={isSplitPane} {...props}>
            {children}
          </StyledLeftPane>
        )
      }}
    </Slot>
  )
}
const StyledLeftPane = styled(Box)<LeftPaneProps & SxProp>`
  display: flex;
  flex-basis: 100%;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  @media (min-width: ${themeGet('breakpoints.1')}) {
    flex-basis: ${p => (p.isSplitPane ? '50%' : '40%')};
  }
  ${sx}
`

// --------------------------------------------------------
// RightPane
export type RightPaneProps = {
  /**
   * Children to render in the nav
   */
  children?: React.ReactNode
  /**
   * Whether the layout should render as a 50/50 split pane; default: 40/60
   */
  isSplitPane?: boolean
} & BoxProps
/**
 * Right pane for Rule builder layout. Must be rendered inside of a Rule Builder
 */
const RightPane = ({children, isSplitPane, ...props}: RightPaneProps) => {
  return (
    <Slot name="RightPane">
      {({isSplitPane}: RuleBuilderLayoutContextType) => {
        return (
          <StyledRightPane isSplitPane={isSplitPane} {...props}>
            {children}
          </StyledRightPane>
        )
      }}
    </Slot>
  )
}
const StyledRightPane = styled(Box)<RightPaneProps & SxProp>`
  display: flex;
  flex-basis: 100%;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  @media (min-width: ${themeGet('breakpoints.1')}) {
    flex-basis: ${p => (p.isSplitPane ? '50%' : '60%')};
  }
  ${sx}
`

// --------------------------------------------------------
// Header
export type HeaderProps = {
  /**
   * Children to render in the header
   */
  children?: React.ReactNode
  /**
   * Whether to hide the header
   */
  hidden?: boolean
} & BoxProps
/**
 * Header for Rule builder layout
 */
const Header = ({children, hidden = false, ...props}: HeaderProps) => {
  return (
    <Slot name="Header">
      {({isSplitPane}: RuleBuilderLayoutContextType) => {
        return <StyledHeader {...props}>{children}</StyledHeader>
      }}
    </Slot>
  )
}
const StyledHeader = styled(Box)<HeaderProps & SxProp>`
  display: flex;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  width: 100%;
  @media (min-width: ${themeGet('breakpoints.2')}) {
  }
  ${sx}
`

// --------------------------------------------------------
// Footer
export type FooterProps = {
  /**
   * A unique label for the rendered contentinfo landmark
   */
  'aria-label'?: React.AriaAttributes['aria-label']
  /**
   * An id to an element which uniquely labels the rendered contentinfo landmark
   */
  'aria-labelledby'?: React.AriaAttributes['aria-labelledby']
  /**
   * Children to render in the footer
   */
  children?: React.ReactNode
  /**
   * Whether to hide the footer
   */
  hidden?: boolean
} & BoxProps
/**
 * Footer for a RuleBuilder Layout. Must be rendered inside of a Rule Builder
 */
const Footer = ({children, hidden = false, ...props}: FooterProps) => {
  return (
    <Slot name="Footer">
      {({isSplitPane}: RuleBuilderLayoutContextType) => {
        return <StyledFooter {...props}>{children}</StyledFooter>
      }}
    </Slot>
  )
}
const StyledFooter = styled(Box)<FooterProps & SxProp>`
  display: flex;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  width: 100%;
  @media (min-width: ${themeGet('breakpoints.2')}) {
  }
  ${sx}
`

RuleBuilderLayout.displayName = 'RuleBuilderLayout'

export default Object.assign(RuleBuilderLayout, {
  Header,
  LeftPane,
  RightPane,
  Footer
})
